import { useEffect, useState, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { isNavItemActive } from './sidebar-active'
import {
  LayoutDashboard,
  Building2,
  Users,
  UserCircle,
  BarChart3,
  FileText,
  MessageSquare,
  MessagesSquare,
  Webhook,
  KeyRound,
  DollarSign,
  CreditCard,
  Bell,
  Workflow,
  Activity,
  ClipboardList,
  ClipboardPen,
  Shield,
  FileCheck,
  ClipboardCheck,
  MessageCircle,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Layers,
  Search,
  Megaphone,
  GitBranch,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { usePermissions } from '@/hooks/usePermissions'
import { ShieldCheck, Ticket, Cpu, Settings2, ListChecks, Send, IdCard } from 'lucide-react'

interface NavItem {
  label: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  /** If set, item only shown when user has this permission */
  permission?: string
}

interface NavGroup {
  label: string
  items: NavItem[]
}

const navGroups: NavGroup[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
      { label: 'Analytics', path: '/analytics', icon: BarChart3, permission: 'analytics:read' },
    ],
  },
  {
    label: 'Management',
    items: [
      { label: 'Tenants', path: '/tenants', icon: Building2, permission: 'tenants:read' },
      { label: 'Users', path: '/users', icon: Users, permission: 'users:read' },
      { label: 'Accounts', path: '/accounts', icon: UserCircle, permission: 'accounts:read' },
    ],
  },
  {
    label: 'Verification',
    items: [
      { label: 'Verifications', path: '/verifications', icon: ListChecks, permission: 'tenants:read' },
      { label: 'Review Queue', path: '/review-queue', icon: ClipboardCheck, permission: 'review:read' },
      { label: 'Blacklist', path: '/blacklist', icon: Shield, permission: 'blacklist:read' },
      { label: 'Content Categories', path: '/content-categories', icon: Layers, permission: 'content:read' },
      { label: 'Document Types', path: '/countries/document-types', icon: IdCard, permission: 'countries:read' },
      { label: 'API Versions', path: '/api-versions', icon: GitBranch, permission: 'api_versions:read' },
      { label: 'OCR Providers', path: '/vlm-providers', icon: Cpu, permission: 'vlm:read' },
      { label: 'VLM Analytics', path: '/vlm-analytics', icon: BarChart3, permission: 'vlm:read' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { label: 'Support', path: '/support', icon: MessageSquare, permission: 'support:read' },
      { label: 'Live Chat', path: '/support/chat', icon: MessagesSquare, permission: 'support:chat' },
      { label: 'Webhook Deliveries', path: '/webhook-deliveries', icon: Send, permission: 'webhooks:read' },
      { label: 'Jobs', path: '/jobs', icon: Workflow, permission: 'jobs:read' },
      { label: 'Audit Log', path: '/audit', icon: FileText, permission: 'audit:read' },
      { label: 'Status', path: '/status', icon: Activity, permission: 'system:read' },
      { label: 'Runtime Settings', path: '/runtime-settings', icon: Settings2, permission: 'settings:read' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { label: 'Campaigns', path: '/marketing', icon: Megaphone, permission: 'marketing:read' },
      { label: 'Review Queue', path: '/marketing/review', icon: ClipboardCheck, permission: 'marketing:read' },
      { label: 'Templates', path: '/marketing/templates', icon: FileText, permission: 'marketing:read' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { label: 'Billing Audit', path: '/billing-audit', icon: DollarSign, permission: 'billing:read' },
      { label: 'Billing Plans', path: '/billing-plans', icon: CreditCard, permission: 'billing:read' },
      { label: 'Coupons', path: '/coupons', icon: Ticket, permission: 'credits:read' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { label: 'Roles', path: '/roles', icon: ShieldCheck, permission: 'roles:manage' },
      { label: 'Notifications', path: '/notifications', icon: Bell, permission: 'notifications:read' },
      { label: 'Webhooks', path: '/webhooks', icon: Webhook, permission: 'webhooks:read' },
      { label: 'OAuth', path: '/oauth', icon: KeyRound, permission: 'oauth:read' },
      { label: 'Surveys', path: '/surveys', icon: ClipboardPen, permission: 'surveys:read' },
      { label: 'Feedback', path: '/feedback', icon: MessageCircle, permission: 'feedback:read' },
      { label: 'Waitlist', path: '/waitlist', icon: ClipboardList, permission: 'waitlist:read' },
      { label: 'DPA', path: '/dpa', icon: FileCheck, permission: 'dpa:read' },
    ],
  },
]

const bottomNavItems: NavItem[] = [
  { label: 'Settings', path: '/settings', icon: Settings },
]

/**
 * Every path the sidebar links to, used to resolve "most specific wins".
 *
 * Some paths are prefixes of others — /marketing precedes /marketing/review and
 * /marketing/templates, /support precedes /support/chat. React Router's NavLink
 * treats a prefix as a match by default, so visiting /marketing/review lit up
 * BOTH Campaigns and Review Queue.
 */
const ALL_NAV_PATHS: string[] = [
  ...navGroups.flatMap((g) => g.items.map((i) => i.path)),
  ...bottomNavItems.map((i) => i.path),
]

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon
  const { pathname } = useLocation()
  // Own the active calculation rather than using NavLink's `isActive`, which
  // prefix-matches and therefore highlights a parent alongside its child.
  const active = isNavItemActive(pathname, item.path, ALL_NAV_PATHS)

  return (
    <NavLink
      to={item.path}
      aria-current={active ? 'page' : undefined}
      className={
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          'hover:bg-accent hover:text-accent-foreground',
          active
            ? 'bg-accent text-accent-foreground'
            : 'text-muted-foreground',
          collapsed && 'justify-center px-2'
        )
      }
      title={collapsed ? item.label : undefined}
    >
      <Icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  )
}

function SidebarGroup({
  group,
  collapsed,
  expanded,
  onToggle,
  visibleItems,
}: {
  group: NavGroup
  collapsed: boolean
  expanded: boolean
  onToggle: () => void
  visibleItems: NavItem[]
}) {
  if (visibleItems.length === 0) return null

  // When sidebar is collapsed, show items without group headers
  if (collapsed) {
    return (
      <div className="space-y-1">
        {visibleItems.map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    )
  }

  const panelId = `sidebar-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`

  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 hover:text-muted-foreground transition-colors"
      >
        <span>{group.label}</span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 transition-transform duration-200',
            !expanded && '-rotate-90'
          )}
          aria-hidden="true"
        />
      </button>
      {/* Collapsing is a CSS height/opacity transition, so the links stay in
          the DOM at zero height. Sighted users see them disappear; keyboard
          users used to keep tabbing through them into an invisible link, and
          screen readers still read them out. `inert` removes them from the tab
          order and the accessibility tree without giving up the animation
          (`hidden` would kill the transition). `aria-hidden` is belt-and-braces
          for the same reason. */}
      <div
        id={panelId}
        inert={!expanded}
        aria-hidden={!expanded}
        className={cn(
          'space-y-1 overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out',
          expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        {visibleItems.map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    </div>
  )
}

function SidebarContent({ collapsed }: { collapsed: boolean }) {
  const { has } = usePermissions()
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(navGroups.map((g) => [g.label, true]))
  )
  const [search, setSearch] = useState('')

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => ({ ...prev, [label]: !prev[label] }))
  }

  // All permission-filtered items across all groups
  const allVisibleItems = useMemo(() => {
    return navGroups.flatMap((group) =>
      group.items.filter((item) => !item.permission || has(item.permission))
    )
  }, [has])

  // Filtered items when search is active
  const searchResults = useMemo(() => {
    if (!search.trim()) return null
    const q = search.toLowerCase()
    return allVisibleItems.filter((item) => item.label.toLowerCase().includes(q))
  }, [search, allVisibleItems])

  return (
    <>
      {/* Brand header */}
      <div
        className={cn(
          'flex h-14 items-center justify-center border-b border-border px-4',
          collapsed ? '' : 'gap-2'
        )}
      >
        {collapsed ? (
          <div
            className="logo-themed h-8 w-8 shrink-0"
            role="img"
            aria-label="Xident"
            style={{ WebkitMaskImage: 'url(/logo.svg)', maskImage: 'url(/logo.svg)' }}
          />
        ) : (
          <div
            className="logo-themed h-7 w-28 shrink-0"
            role="img"
            aria-label="Xident Admin"
            style={{ WebkitMaskImage: 'url(/logo-xident.svg)', maskImage: 'url(/logo-xident.svg)' }}
          />
        )}
      </div>

      {/* Search input — only when expanded */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-lg border border-border bg-background pl-8 pr-8 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              aria-label="Search navigation"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main navigation — grouped and filtered by user's permissions */}
      <nav className="flex-1 space-y-3 overflow-y-auto p-3">
        {searchResults !== null ? (
          /* Flat search results */
          searchResults.length > 0 ? (
            <div className="space-y-1">
              {searchResults.map((item) => (
                <SidebarLink key={item.path} item={item} collapsed={collapsed} />
              ))}
            </div>
          ) : (
            <p className="px-3 py-2 text-xs text-muted-foreground">No results</p>
          )
        ) : (
          /* Normal grouped navigation */
          navGroups.map((group) => {
            const visibleItems = group.items.filter((item) => !item.permission || has(item.permission))
            return (
              <SidebarGroup
                key={group.label}
                group={group}
                collapsed={collapsed}
                expanded={expandedGroups[group.label] ?? true}
                onToggle={() => toggleGroup(group.label)}
                visibleItems={visibleItems}
              />
            )
          })
        )}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-border p-3 space-y-1">
        {bottomNavItems.map((item) => (
          <SidebarLink key={item.path} item={item} collapsed={collapsed} />
        ))}
      </div>
    </>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, setMobileSidebarOpen } = useUIStore()
  const location = useLocation()

  // Auto-close mobile drawer on route change
  useEffect(() => {
    setMobileSidebarOpen(false)
  }, [location.pathname, setMobileSidebarOpen])

  return (
    <>
      {/* Desktop sidebar — hidden on mobile */}
      <aside
        className={cn(
          'hidden lg:flex fixed inset-y-0 left-0 z-30 flex-col border-r border-border bg-card transition-all duration-200',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        <SidebarContent collapsed={sidebarCollapsed} />

        {/* Collapse toggle — desktop only */}
        <div className="border-t border-border p-3">
          <button
            onClick={toggleSidebar}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors',
              'hover:bg-accent hover:text-accent-foreground',
              sidebarCollapsed && 'justify-center px-2'
            )}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-5 w-5 shrink-0" />
            ) : (
              <>
                <ChevronLeft className="h-5 w-5 shrink-0" />
                <span className="truncate">Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile drawer overlay */}
      <div
        className={cn(
          'lg:hidden fixed inset-0 z-40 transition-visibility',
          mobileSidebarOpen ? 'visible' : 'invisible delay-300'
        )}
      >
        {/* Backdrop */}
        <div
          className={cn(
            'absolute inset-0 bg-black/50 transition-opacity duration-300',
            mobileSidebarOpen ? 'opacity-100' : 'opacity-0'
          )}
          onClick={() => setMobileSidebarOpen(false)}
        />

        {/* Slide-in panel */}
        <aside
          className={cn(
            'absolute inset-y-0 left-0 z-50 flex w-64 flex-col bg-card shadow-xl transition-transform duration-300 ease-in-out',
            mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          <SidebarContent collapsed={false} />

          {/* Close button */}
          <div className="border-t border-border p-3">
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              aria-label="Close sidebar"
            >
              <X className="h-5 w-5 shrink-0" />
              <span className="truncate">Close</span>
            </button>
          </div>
        </aside>
      </div>
    </>
  )
}
